/**
 * Sentry's default ui.click / ui.input breadcrumbs list every CSS class on the
 * clicked element (e.g. button.joy-button.joy-button_ghost.joy-button_small...).
 * On asset-heavy pages those crumbs can fill Sentry's 100-breadcrumb cap and hide
 * useful context. Example: https://malt.sentry.io/issues/MALT-3MME/
 *
 * This module rewrites those messages to stable, readable selectors aligned with
 * Malt conventions: nearest data-testid or id, else a compact aria-label fallback.
 */

type DomNode = {
    tagName: string;
    getAttribute(name: string): string | null;
    parentElement: DomNode | null;
};

const UI_INTERACTION_CATEGORIES = new Set(['ui.click', 'ui.input']);
const FALLBACK_ATTRIBUTES = ['aria-label', 'type', 'name', 'title'] as const;

export function simplifyUiInteractionBreadcrumb(
    breadcrumb: {category?: string; message?: string},
    hint?: {event?: unknown},
): {category?: string; message?: string} {
    if (!breadcrumb.category || !UI_INTERACTION_CATEGORIES.has(breadcrumb.category)) {
        return breadcrumb;
    }

    const target = getClickTarget(hint?.event);
    if (!target) {
        return breadcrumb;
    }

    return {...breadcrumb, message: getUiInteractionLabel(target)};
}

function getClickTarget(event: unknown): DomNode | null {
    if (!event || typeof event !== 'object' || !('target' in event)) {
        return null;
    }

    const {target} = event;
    if (!target || typeof target !== 'object' || typeof (target as DomNode).getAttribute !== 'function') {
        return null;
    }

    return target as DomNode;
}

/** Walk up the DOM for data-testid/id; fall back to semantic attrs on the clicked element. */
function getUiInteractionLabel(element: DomNode): string {
    for (let node: DomNode | null = element; node; node = node.parentElement) {
        const testId = node.getAttribute('data-testid');
        if (testId) {
            return `${node.tagName.toLowerCase()}[data-testid="${testId}"]`;
        }

        const id = node.getAttribute('id');
        if (id) {
            return `${node.tagName.toLowerCase()}#${id}`;
        }
    }

    const tag = element.tagName.toLowerCase();
    const attributes = FALLBACK_ATTRIBUTES.flatMap((name) => {
        const value = element.getAttribute(name);
        return value ? [`[${name}="${value}"]`] : [];
    });

    return `${tag}${attributes.join('')}`;
}
